// Images and videos: the host's media library or an upload endpoint (uploadUrl, or an upload function).
// Without any, files of the computer are refused: a picture stored in the page as base64 would fill the
// database with megabytes of text. The inlineImages option allows it on purpose (pages without a server).
import { safeUrl } from './sanitize.js';

const ACCEPT = { image: 'image/png,image/jpeg,image/gif,image/webp,image/avif,image/svg+xml', video: 'video/mp4,video/webm' };

function pickFile(type) {
    return new Promise(resolve => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = ACCEPT[type] || ACCEPT.image;
        input.style.display = 'none';
        input.addEventListener('change', () => {
            resolve(input.files && input.files[0] ? input.files[0] : null);
            input.remove();
        }, { once: true });
        // No change event when the dialog is closed without a file: the promise simply never resolves.
        document.body.append(input);
        input.click();
    });
}

/** Reads an image and returns a resized data URL (WebP when the browser can write it). */
export function resizeImage(file, maxSize = 1600, quality = 0.85) {
    return new Promise((resolve, reject) => {
        if (file.type === 'image/svg+xml' || file.type === 'image/gif') {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
            return;
        }
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const ratio = Math.min(1, maxSize / Math.max(img.naturalWidth, img.naturalHeight));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(img.naturalWidth * ratio));
            canvas.height = Math.max(1, Math.round(img.naturalHeight * ratio));
            canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
            URL.revokeObjectURL(url);
            let data = canvas.toDataURL('image/webp', quality);
            if (!data.startsWith('data:image/webp')) data = canvas.toDataURL('image/jpeg', quality);
            resolve(data);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('unreadable image'));
        };
        img.src = url;
    });
}

function normalizeResult(result) {
    if (!result) return null;
    if (typeof result === 'string') return { url: result };
    const url = result.optimized_url || result.url || result.location || (result.data && result.data.url) || '';
    return url ? { ...result, url } : null;
}

export class Media {
    constructor(editor) {
        this.editor = editor;
    }

    get options() {
        return this.editor.options;
    }

    hasLibrary() {
        const library = this.options.mediaLibrary;
        return !!(library && typeof library.open === 'function');
    }

    /** Whether files of the computer can be sent (or, with inlineImages, kept in the page). */
    canUpload(type = 'image') {
        const o = this.options;
        return typeof o.upload === 'function' || !!o.uploadUrl || (type === 'image' && !!o.inlineImages);
    }

    /** Whether the media field offers a button (a library to choose from, or files to send). */
    canPick(type = 'image') {
        return this.hasLibrary() || this.canUpload(type);
    }

    /**
     * Opens the media library of the host, or a file dialog followed by an upload.
     * onStart() is called once a file is chosen, when its upload starts.
     */
    async pick(type = 'image', { onStart } = {}) {
        if (this.hasLibrary()) {
            const result = normalizeResult(await this.options.mediaLibrary.open({ type }));
            if (result && safeUrl(result.url, { image: true })) return result;
            return null;
        }
        if (!this.canUpload(type)) {
            this.editor.notify(this.editor.t('error.noUpload'), 'warning', 8000);
            return null;
        }
        const file = await pickFile(type);
        if (!file) return null;
        if (onStart) onStart(file);
        return this.upload(file, type);
    }

    async upload(file, type = 'image') {
        const { t } = this.editor;
        const max = this.options.maxUploadSize || 10 * 1024 * 1024;
        const expected = type === 'video' ? /^video\// : /^image\//;
        if (!expected.test(file.type)) {
            this.editor.notify(t('error.fileType'), 'error');
            return null;
        }
        if (file.size > max) {
            this.editor.notify(t('error.fileSize', { size: Math.round(max / 1048576) }), 'error');
            return null;
        }
        if (!this.canUpload(type)) {
            this.editor.notify(t('error.noUpload'), 'warning', 8000);
            return null;
        }
        try {
            let result = null;
            if (typeof this.options.upload === 'function') {
                result = normalizeResult(await this.options.upload(file, { type }));
            } else if (this.options.uploadUrl) {
                result = normalizeResult(await this.post(file));
            } else {
                // inlineImages: the picture is resized in the browser and kept in the page.
                result = { url: await resizeImage(file, this.options.imageMaxSize || 1600), name: file.name };
            }
            if (!result || !safeUrl(result.url, { image: true })) throw new Error(t('error.upload'));
            if (!result.alt && file.name) result.label = result.label || file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
            this.editor.emit('upload', { file, result });
            return result;
        } catch (error) {
            this.editor.notify(error && error.message ? error.message : t('error.upload'), 'error', 6000);
            this.editor.emit('upload:error', { file, error });
            return null;
        }
    }

    async post(file) {
        const o = this.options;
        const data = new FormData();
        data.append(o.uploadField || 'upload', file);
        if (o.csrfToken) data.append(o.csrfField || 'csrf_token', o.csrfToken);
        if (o.module) data.append('module', o.module);
        for (const [key, value] of Object.entries(o.uploadData || {})) data.append(key, value);
        const headers = { Accept: 'application/json', ...(o.uploadHeaders || {}) };
        if (o.csrfToken) headers['X-CSRF-Token'] = o.csrfToken;
        const response = await fetch(o.uploadUrl, { method: 'POST', body: data, headers, credentials: 'same-origin' });
        let json = null;
        try {
            json = await response.json();
        } catch (error) {
            json = null;
        }
        if (!response.ok || !json || json.error) {
            const message = json && json.error ? (json.error.message || json.error) : `HTTP ${response.status}`;
            throw new Error(String(message));
        }
        return json;
    }
}
